/**
 * The scenario the prototype is tried against, on a card over every page:
 * dragged by its header, dismissed with its close button and shown again from
 * the preview panel. Not part of Track's UI — it goes with the rest of
 * src/prototypes/modular-contract-builder/.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { DotsGrid, XClose } from "@untitledui/icons";
import { Button } from "@/components/ui/button";

const SCENARIO = [
  "Record label signs a 4 track EP deal with an artist. The contract stipulates the recorded music royalties split between the label and the payee is 50/50 for all formats, except vinyl, where the label gets 70% and the artist gets 30%.",
  "The contract also states the label also advances the payee $5,000, with only 50% of the advance recoupable.",
  "This contract covers all 4 tracks of the release.",
  "However for track 2, the artist collaborated with another producer, and featured singer. The producer gets 25% of the royalties, which is taken from the Artists share, and the singer gets 3% which is taken from both the artist and the labels share.",
  "The featured producer was also advanced $1,000, 100% recoupable.",
];

/** The scenario card is hidden for now: set to true to bring it (and "Show scenario") back. */
export const SCENARIO_ENABLED = false;

const WIDTH = 360;
const MARGIN = 16;
const HIDDEN_KEY = "prototype-scenario-hidden";
const POSITION_KEY = "prototype-scenario-position";

// Shown or not, shared by the card and the preview panel's "Show scenario".
const listeners = new Set<() => void>();
const read = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // Storage off: this session just won't remember it.
  }
};
const setHidden = (hidden: boolean) => {
  write(HIDDEN_KEY, hidden ? "1" : null);
  for (const listener of listeners) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Whether the scenario card is shown, and a way to bring it back. */
export function useScenario() {
  const hidden = useSyncExternalStore(subscribe, () => read(HIDDEN_KEY) === "1");
  return { shown: !hidden, show: () => setHidden(false) };
}

type Position = { x: number; y: number };

/** Kept inside the window, so the card can't be dragged out of reach. */
const clamp = ({ x, y }: Position, height: number): Position => ({
  x: Math.min(Math.max(x, MARGIN), window.innerWidth - WIDTH - MARGIN),
  y: Math.min(Math.max(y, MARGIN), Math.max(MARGIN, window.innerHeight - height - MARGIN)),
});

export function ScenarioCard() {
  const { shown } = useScenario();
  const card = useRef<HTMLElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  // Top right, below the top bar, unless it was moved this session.
  const [position, setPosition] = useState<Position>(() => {
    const saved = read(POSITION_KEY);
    if (saved) {
      try {
        return JSON.parse(saved) as Position;
      } catch {
        // Fall through to the default.
      }
    }
    return { x: window.innerWidth - WIDTH - 24, y: 80 };
  });

  // The window got smaller: bring the card back in.
  useEffect(() => {
    const keepIn = () => setPosition((current) => clamp(current, card.current?.offsetHeight ?? 0));
    keepIn();
    window.addEventListener("resize", keepIn);
    return () => window.removeEventListener("resize", keepIn);
  }, []);

  if (!SCENARIO_ENABLED || !shown) return null;

  return (
    <section
      ref={card}
      aria-label="Scenario"
      className="fixed z-50 flex max-h-[calc(100vh-2rem)] flex-col rounded-md border border-border bg-background shadow-md"
      style={{ left: position.x, top: position.y, width: WIDTH }}
    >
      {/* A form card's header, and the handle to drag the card by. */}
      <div
        className="flex cursor-grab touch-none select-none items-center gap-2 rounded-t-md border-b border-border bg-subtle py-1.5 pr-1.5 pl-3 active:cursor-grabbing"
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).closest("button")) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { dx: e.clientX - position.x, dy: e.clientY - position.y };
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          setPosition(
            clamp(
              { x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy },
              card.current?.offsetHeight ?? 0,
            ),
          );
        }}
        onPointerUp={() => {
          drag.current = null;
          write(POSITION_KEY, JSON.stringify(position));
        }}
      >
        <DotsGrid className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="flex-1 font-mono text-sm font-medium">Scenario</span>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label="Dismiss scenario"
          onClick={() => setHidden(true)}
        >
          <XClose className="size-4" />
        </Button>
      </div>
      <div className="flex flex-col gap-3 overflow-y-auto p-4 text-sm">
        <p className="font-medium">Here's a use case example:</p>
        {SCENARIO.map((paragraph) => (
          <p key={paragraph} className="text-muted-foreground">
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
}
